import CheckSharpIcon from "@mui/icons-material/CheckSharp";
import CloseIcon from "@mui/icons-material/Close";
import PreviewIcon from "@mui/icons-material/Preview";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Link,
  Skeleton,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import { useContext, useEffect, useState } from "react";
import { ConstantContext } from "components/context/ConstantProvider";
import { Form } from "components/shared/Form";
import {
  SelectAutocomplete,
  SelectAutocompleteTextField,
} from "components/ui/atoms/SelectAutocomplete";
import { SnackbarContext } from "components/context/SnackbarProvider";
import WithConfirmationButton from "components/ui/atoms/WithConfirmationButton";
import DictionaryField from "components/ui/atoms/formFields/DictionaryField";
import UserField from "components/ui/atoms/formFields/UserField";
import { useAllCommitteeMinute } from "models/committee/apiCommitteeMinute";
import { useValidateCommitteeMeeting } from "models/deals/apiDeal";

const COMMITTEE_MINUTE_POLL_INTERVAL_MS = 3000;
const COMMITTEE_MINUTE_POLL_TIMEOUT_MS = 5 * 60 * 1000;

const asArray = (obj) => {
  return Array.isArray(obj) ? obj : obj ? [obj] : [];
};

const CommitteeFilterAndSort = ({ state, setState, onClose, refetchList }) => {
  const theme = useTheme();
  const { displayMessage, displayError } = useContext(SnackbarContext);

  const { dealStatuses, dealFlows, dealCommitteeState } =
    useContext(ConstantContext);

  const [pendingCommitteeMinuteName, setPendingCommitteeMinuteName] =
    useState(null);

  const {
    mutateAsync: mutateValidateDealCommitteeAsync,
    isPending: validateDealCommitteeIsPending,
  } = useValidateCommitteeMeeting();

  const { data: committeeMinuteList, isLoading: committeeMinuteListIsLoading } =
    useAllCommitteeMinute(
      { deal_flow: state?.deal_flow },
      {
        refetchInterval: pendingCommitteeMinuteName
          ? COMMITTEE_MINUTE_POLL_INTERVAL_MS
          : false,
      },
    );

  useEffect(() => {
    if (
      !pendingCommitteeMinuteName ||
      !committeeMinuteList?.some(
        (committeeMinute) =>
          committeeMinute.name === pendingCommitteeMinuteName,
      )
    ) {
      return;
    }
    setPendingCommitteeMinuteName(null);
    refetchList && refetchList();
    displayMessage("Committee minute created", <>&#127881;</>, "success");
  }, [committeeMinuteList, pendingCommitteeMinuteName]);

  useEffect(() => {
    if (!pendingCommitteeMinuteName) {
      return;
    }
    const timeout = setTimeout(() => {
      setPendingCommitteeMinuteName(null);
      displayMessage(
        "Committee minute is still being created, check again later",
      );
    }, COMMITTEE_MINUTE_POLL_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [pendingCommitteeMinuteName]);

  const validateCommittee = () => {
    return mutateValidateDealCommitteeAsync(state?.deal_flow)
      .then(({ name }) => {
        setPendingCommitteeMinuteName(name);
        displayMessage("Committee minute is being created");
      })
      .catch((error) => {
        displayError(error);
      });
  };

  const handleOnChangeCommitteeMinute = (e, value) => {
    if (value) {
      window.open(`/committee_minute/${value.id}`, "_blank");
    }
  };

  return (
    <Card
      sx={{
        backgroundColor: `${theme.palette.secondary.extralight}`,
        my: 1,
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 1,
          }}
        >
          <Typography variant="h6">Committee</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Grid container spacing={1} p={1}>
          <Form
            prepareInitialValues={(object) => ({
              owners: asArray(object?.owners),
              deal_flow: asArray(object?.deal_flow),
              investment_status: asArray(object?.investment_status),
              committee_state: asArray(object?.committee_state),
            })}
            onSubmit={(value) => {
              setState(value);
              return state;
            }}
            values={state}
            autoSubmit
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <UserField name="owners" label="Owner" multiple />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <DictionaryField
                optionsDictionary={Object.fromEntries(
                  dealStatuses.map((x) => [x.code, x.long]),
                )}
                multiple
                name="investment_status"
                label="Investment Status"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <DictionaryField
                optionsDictionary={Object.fromEntries(
                  dealFlows.map((x) => [x.id, x.name]),
                )}
                multiple
                name="deal_flow"
                label="Dealflow"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <DictionaryField
                optionsDictionary={Object.fromEntries(
                  dealCommitteeState.map((x) => [x.value, x.label]),
                )}
                multiple
                name="committee_state"
                label="Committee state"
              />
            </Grid>
          </Form>
        </Grid>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 1,
          }}
        >
          <Typography variant="h6">Previous committee</Typography>
        </Box>
        <Grid container spacing={1} p={1}>
          <Grid size={{ xs: 12, md: 6 }}>
            {!committeeMinuteListIsLoading ? (
              <SelectAutocomplete
                id="committee_minute-select"
                options={committeeMinuteList}
                onChange={handleOnChangeCommitteeMinute}
                getOptionLabel={(option) => `${option.name}`}
                renderInput={(params) => (
                  <SelectAutocompleteTextField
                    {...params}
                    label="Previous committee minutes"
                  />
                )}
                disabled={
                  !state || Array.isArray(state?.deal_flow) || !state?.deal_flow
                }
              />
            ) : (
              <Skeleton variant="rectangular" height={40} />
            )}
          </Grid>
          <Grid container size={12} spacing={1}>
            <Grid ml={"auto"}>
              <Link
                style={{ textDecoration: "none", color: "inherit" }}
                to={{
                  pathname: `/committee_minute/preview/${state?.deal_flow}`,
                }}
              >
                <Button
                  variant="outlined"
                  endIcon={<PreviewIcon sx={{ scale: "0.8" }} />}
                  disabled={
                    !state ||
                    Array.isArray(state?.deal_flow) ||
                    !state?.deal_flow
                  }
                >
                  Preview
                </Button>
              </Link>
            </Grid>
            <Grid>
              <WithConfirmationButton
                confirmationMessage="Confirm committee"
                variant="outlined"
                endIcon={<CheckSharpIcon sx={{ scale: "0.8" }} />}
                onClick={validateCommittee}
                loading={
                  validateDealCommitteeIsPending || !!pendingCommitteeMinuteName
                }
                disabled={
                  !state || Array.isArray(state?.deal_flow) || !state?.deal_flow
                }
              >
                Validate committee
              </WithConfirmationButton>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CommitteeFilterAndSort;
